import React, { createContext, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { getCookie, setCookie, deleteCookie } from '../../shared/utils/cookies'

export type DriveTypeFilter = 'all' | 'folders' | 'documents' | 'code' | 'text' | 'media'
export type DriveSortField = 'name' | 'date' | 'size'
export type DriveSortDirection = 'asc' | 'desc'

export type DriveTypeCounts = Record<Exclude<DriveTypeFilter, 'all'>, number>

const EMPTY_TYPE_COUNTS: DriveTypeCounts = {
    folders: 0,
    documents: 0,
    code: 0,
    text: 0,
    media: 0,
};

type DriveViewCtxType = {
    search: string;
    setSearch: React.Dispatch<React.SetStateAction<string>>;
    typeFilter: DriveTypeFilter;
    setTypeFilter: React.Dispatch<React.SetStateAction<DriveTypeFilter>>;
    sortField: DriveSortField;
    setSortField: React.Dispatch<React.SetStateAction<DriveSortField>>;
    sortDirection: DriveSortDirection;
    setSortDirection: React.Dispatch<React.SetStateAction<DriveSortDirection>>;
    folderId: string | null;
    setFolderId: React.Dispatch<React.SetStateAction<string | null>>;
    typeCounts: DriveTypeCounts;
    setTypeCounts: React.Dispatch<React.SetStateAction<DriveTypeCounts>>;
}

const DriveViewContext = createContext<DriveViewCtxType | null>(null);

// Remembers the folder you were browsing in Drive mode so a page refresh drops
// you back where you were, mirroring Google Drive. Only meaningful across a
// refresh: if the room was last loaded in Editor mode, the saved folder is
// stale (the user never got to navigate away from it in Drive), so it's
// revoked rather than silently reused the next time Drive mode is opened.
const driveFolderCookieName = (roomId: string) => `flashroom_drive_folder_${roomId}`;

export const DriveViewProvider = ({ children }: { children: React.ReactNode }) => {
    const { roomId } = useParams();
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState<DriveTypeFilter>('all');
    const [sortField, setSortField] = useState<DriveSortField>('name');
    const [sortDirection, setSortDirection] = useState<DriveSortDirection>('asc');
    const [folderId, setFolderId] = useState<string | null>(() => {
        if (!roomId) return null;

        const wasInDriveMode = localStorage.getItem('flashroom-ui-mode') === 'files';
        if (!wasInDriveMode) {
            deleteCookie(driveFolderCookieName(roomId));
            return null;
        }

        return getCookie(driveFolderCookieName(roomId));
    });
    const [typeCounts, setTypeCounts] = useState<DriveTypeCounts>(EMPTY_TYPE_COUNTS);

    useEffect(() => {
        if (!roomId) return;

        if (folderId) {
            setCookie(driveFolderCookieName(roomId), folderId, { days: 30 });
        } else {
            deleteCookie(driveFolderCookieName(roomId));
        }
    }, [roomId, folderId]);

    return (
        <DriveViewContext.Provider value={{
            search, setSearch,
            typeFilter, setTypeFilter,
            sortField, setSortField,
            sortDirection, setSortDirection,
            folderId, setFolderId,
            typeCounts, setTypeCounts,
        }}>
            {children}
        </DriveViewContext.Provider>
    );
};

export function useDriveView() {
    const ctx = useContext(DriveViewContext);
    if (!ctx) throw new Error("useDriveView must be used within DriveViewProvider");
    return ctx;
}
